var _process$env;
import { schemas } from '#oaf_schema.registry';
import { createStore, createWireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { ListingEditorStatus, getListingEditorStatus } from '~/modules/utilities/listingEditorStatus';
import { ModelTypes } from '../services/pricing/models/Pricing';
import { retrievePricing, savePricing as savePricingService, validatePricing } from '../services/pricing/pricingService';
import { notifyFailure } from '../utilities/notificationUtilities';
import { ZERO_DECIMAL_CURRENCIES, createNewDefaultPlan, createUIStateForPlan, getSchemaForPricingModelType, isPricingProjectionValid, pricingModelToRecord } from '../utilities/pricingUtilities';
import { BUSINESS_APPROVAL_STATUSES, approvalsStore, selectApprovalStatus } from './approvalsStore';
import { i18nStore } from './i18nStore';
import { routerStore, selectShowInputErrors } from './routerStore';
const DEFAULT_MODEL = schemas['pricing_plan/pricing/pricing_model/Free'].new({});
const initialState = {
  listingId: undefined,
  priceModelType: undefined,
  model: DEFAULT_MODEL,
  commerceProductId: '',
  paymentType: '',
  error: undefined,
  isLoading: false,
  isSaving: false,
  hasChanged: false,
  initialPriceModelType: undefined,
  initialModel: DEFAULT_MODEL,
  initialPaymentType: '',
  cachedHasChanged: false,
  isResubmissionRequired: false,
  isAbleToEditOneTimePayments: false
};
export const pricingStore = createStore(initialState, (set, get) => ({
  /**
   * @returns {boolean} true if pricing initialized successfuly, false if there was an error
   */
  async initialize({
    id: listingId,
    newListingType = 'force/package',
    currentListing,
    listingApprovalStatus
  }) {
    const currentState = get();
    let error = currentState.error;
    let hasChanged = currentState.hasChanged;
    let priceModelType = currentState.priceModelType;
    let model = currentState.model;
    let paymentType = currentState.paymentType;
    let initialModel = currentState.initialModel;
    let initialPriceModelType = currentState.initialPriceModelType;
    let initialPaymentType = currentState.initialPaymentType;
    let commerceProductId = currentState.commerceProductId;
    let isAbleToEditOneTimePayments = currentState.isAbleToEditOneTimePayments;
    function resetPricingData() {
      model = initialState.model;
      paymentType = initialState.paymentType;
      priceModelType = initialState.priceModelType;
      initialModel = initialState.initialModel;
      initialPriceModelType = initialState.initialPriceModelType;
      initialPaymentType = initialState.initialPaymentType;
      approvalsStore.reset();
    }
    const editorStatus = getListingEditorStatus(listingId, newListingType, currentListing);
    switch (editorStatus) {
      case ListingEditorStatus.DifferentListing:
        {
          set(function loading(draft) {
            draft.isLoading = true;
          });
          try {
            error = initialState.error;
            hasChanged = initialState.hasChanged;
            const pricingData = await retrievePricing(listingId);
            if (isPricingProjectionValid(pricingData)) {
              var _pricingData$commerce, _model, _model2, _pricingData$approval, _pricingData$approval2, _pricingData$approval3;
              model = pricingData.model;
              // We store UI STATE on the plans array to make it easier to handle re-ordering and displaying input errors, this field gets removed in pricingModelToRecord
              if (model && Array.isArray(model.plans)) {
                // Re order plans by index in case it came in different order than index after Tz migration
                reOrderByIndex(model.plans);
                model.plans = model.plans.map((plan, index) => ({
                  ...plan,
                  index,
                  __UI_STATE__: createUIStateForPlan(plan)
                }));
              }
              priceModelType = pricingData.price_model_type;
              commerceProductId = (_pricingData$commerce = pricingData.commerce_external_id) !== null && _pricingData$commerce !== void 0 ? _pricingData$commerce : '';
              paymentType = (_model = model) === null || _model === void 0 ? void 0 : _model.payment_type;
              initialPriceModelType = pricingData.price_model_type;
              initialModel = pricingData.model;
              initialPaymentType = (_model2 = model) === null || _model2 === void 0 ? void 0 : _model2.payment_type;
              isAbleToEditOneTimePayments = paymentType === 'salesforce_provided' || doesPlansHaveOneTime(model.plans);
              let status = pricingData === null || pricingData === void 0 ? void 0 : (_pricingData$approval = pricingData.approval) === null || _pricingData$approval === void 0 ? void 0 : _pricingData$approval.status;
              if (status === BUSINESS_APPROVAL_STATUSES.Draft && ((_pricingData$approval2 = pricingData.approval) === null || _pricingData$approval2 === void 0 ? void 0 : _pricingData$approval2.org62_id) == null && listingApprovalStatus != null) {
                status = listingApprovalStatus;
              }
              const message = pricingData === null || pricingData === void 0 ? void 0 : (_pricingData$approval3 = pricingData.approval) === null || _pricingData$approval3 === void 0 ? void 0 : _pricingData$approval3.message;
              approvalsStore.initialize({
                status,
                message
              });
            } else {
              console.info(`Failed to find pricing data for listing with id=${listingId}`);
              resetPricingData();
              approvalsStore.reset();
            }
          } catch (e) {
            if (e.status === 404) {
              console.info(`Failed to find pricing data for listing with id=${listingId}`);
              resetPricingData();
              approvalsStore.reset();
            } else {
              console.error('Pricing Service Error: ', e);
              error = e;
            }
          }
          break;
        }
      case ListingEditorStatus.NewListing:
        {
          set(function loading(draft) {
            draft.isLoading = true;
          });
          resetPricingData();
          approvalsStore.reset();
          error = initialState.error;
          hasChanged = initialState.hasChanged;
          break;
        }
      default:
        // If we're not dealing with a new or different listing, we don't need to re-initialize the store
        return;
    }
    set(function initializing(draft) {
      draft.listingId = listingId;
      draft.error = error;
      draft.model = model;
      draft.paymentType = paymentType;
      draft.priceModelType = priceModelType;
      draft.commerceProductId = commerceProductId;
      draft.hasChanged = hasChanged;
      draft.initialPriceModelType = initialPriceModelType;
      draft.initialModel = initialModel;
      draft.initialPaymentType = initialPaymentType;
      draft.isAbleToEditOneTimePayments = isAbleToEditOneTimePayments;
    });

    // We must set loading to false in a separate set action to ensure the hasChanged boolean isn't set to true when we are initializing the values
    set(function initialized(draft) {
      draft.isLoading = false;
    });
  },
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  change() {
    set(function change(draft) {
      draft.hasChanged = true;
    });
  },
  resetHasChanged() {
    set(function resetHasChanged(draft) {
      draft.hasChanged = false;
    });
  },
  /* istanbul ignore next */
  setIsSaving(value) {
    set(function setIsSaving(draft) {
      draft.isSaving = value;
    });
  },
  /* istanbul ignore next */
  setPriceModelType(value) {
    set(function setPriceModelType(draft) {
      draft.priceModelType = value;
      // If we select the same price model type as it was before, we keep the model as the previous one.
      if (draft.initialPriceModelType === value) {
        draft.model = draft.initialModel;
        draft.paymentType = draft.initialPaymentType;
        return;
      }
      draft.model = getSchemaForPricingModelType(value).new();
      draft.paymentType = '';
      if (value === ModelTypes.Paid) {
        draft.model.payment_collection = '';
        draft.model.payment_type = '';
      } else {
        draft.model.payment_collection = undefined;
        draft.model.payment_type = undefined;
      }

      // If we change the pricing model to any different from free, we want to display the plan names
      if (value !== ModelTypes.Free) {
        draft.model.display_plan_names = true;
      }

      // If we're changing pricing model and the plans array is missing or empty, initialize it with the default plan
      if (value !== ModelTypes.Free && value !== ModelTypes.Paid && (!draft.model.plans || draft.model.plans.length === 0)) {
        draft.model.plans = [createNewDefaultPlan()];
      }
    });
  },
  /* istanbul ignore next */
  setPaymentTypeChange(paymentType) {
    set(function setPaymentTypeChange(draft) {
      draft.paymentType = paymentType;
      const hasPriceModelTypeChanged = draft.initialPriceModelType !== draft.priceModelType;
      const hasPaymentTypeChanged = draft.initialPaymentType !== paymentType;
      if (!hasPriceModelTypeChanged && !hasPaymentTypeChanged) {
        draft.model = draft.initialModel;
        return;
      }
      if (paymentType === 'salesforce_provided') {
        draft.model = schemas[`pricing_plan/pricing/pricing_model/Paid`].new();
        draft.model.payment_collection = '';
        draft.model.display_plan_names = true;
      } else {
        draft.model = schemas[`pricing_plan/pricing/pricing_model/PaidWithoutCheckout`].new();
        draft.model.payment_collection = undefined;
        draft.model.display_plan_names = true;
      }

      // If we're changing pricing model and the plans array is missing or empty, initialize it with the default plan
      if (paymentType !== 'salesforce_provided' && (!draft.model.plans || draft.model.plans.length === 0)) {
        draft.model.plans = [createNewDefaultPlan()];
      }
    });
  },
  /* istanbul ignore next */
  setPaymentCollectionChange(value) {
    set(function setPaymentCollectionChange(draft) {
      draft.model.payment_collection = value;
    });
  },
  /* istanbul ignore next */
  setPaymentMethodChange(values) {
    set(function setPaymentMethodChange(draft) {
      draft.model.payment_method = values;
    });
  },
  // TODO: I'm not a fan of using this setProperty to dynamically modify state in the data model.
  // String keys are hard to debug when things go wrong, it's better to have a separate set function for each field.
  // Even though that's more work initially, keeping it simple and statically typed will pay off in the long run.
  /* istanbul ignore next */
  setProperty(key, value) {
    set(function setProperty(draft) {
      if (typeof value === 'string') {
        draft.model[key] = value.trim();
      } else {
        draft.model[key] = value;
      }
    });
  },
  /* istanbul ignore next */
  setDiscountsOffered(value) {
    set(function setDiscountsOffered(draft) {
      draft.model.discounts_offered = value;
    });
  },
  /* istanbul ignore next */
  setPlanProperty(key, value, index) {
    set(function setPlanProperty(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        if (typeof value === 'string') {
          draft.model.plans[index][key] = value.trim();
        } else {
          draft.model.plans[index][key] = value;
        }
      }
    });
  },
  /* istanbul ignore next */
  setPlanName(index, value) {
    set(function setPricePlan(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        const plan = draft.model.plans[index];
        plan.plan_name = value;
        plan.__UI_STATE__.requiredErrors.plan_name = !plan.plan_name;
      }
    });
  },
  /* istanbul ignore next */
  setTrialDays(index, value) {
    set(function setTrialDays(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        draft.model.plans[index].trial_days = value;
      }
    });
  },
  /* istanbul ignore next */
  setPricePlan(index, value) {
    set(function setPricePlan(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        const plan = draft.model.plans[index];
        plan.price = value;
      }
    });
  },
  /* istanbul ignore next */
  setCustomUnits(index, value) {
    set(function setCustomUnits(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        const plan = draft.model.plans[index];
        plan.custom_unit = value.trim();
        plan.__UI_STATE__.requiredErrors.units = plan.units === 'custom' && !plan.custom_unit || !plan.units;
      }
    });
    this.setRequireResubmission();
  },
  // Unit and CustomUnits need to be handle together since custom_unit will depend on unit when this is "custom"
  /* istanbul ignore next */
  setUnitsAndCustomUnits(index, units, customUnits) {
    set(function setCustomUnits(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        const plan = draft.model.plans[index];
        plan.units = units;
        plan.custom_unit = customUnits.trim();
        plan.__UI_STATE__.requiredErrors.units = plan.units === 'custom' && !plan.custom_unit || !plan.units;
      }
    });
    this.setRequireResubmission();
  },
  /* istanbul ignore next */
  setCurrency(index, value) {
    set(function setCurrency(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        // If change from a currency to another then clean out price
        if (value !== '' && value !== draft.model.plans[index].currency_code && draft.model.plans[index].currency_code !== '') {
          draft.model.plans[index].price = undefined;
        }
        // Remove decimal places from price when currency does not allow
        if (ZERO_DECIMAL_CURRENCIES.includes(value) && !Number.isInteger(draft.model.plans[index].price)) {
          draft.model.plans[index].price = Math.round(draft.model.plans[index].price);
        }
        const plan = draft.model.plans[index];
        plan.currency_code = value;
      }
    });
  },
  /* istanbul ignore next */
  setFrequency(index, value) {
    set(function setFrequency(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        const plan = draft.model.plans[index];
        plan.frequency = value;
        plan.__UI_STATE__.requiredErrors.frequency = !plan.frequency;
      }
    });
  },
  reOrderPrices(indexToChange, indexTarget) {
    set(function reOrderPrice(draft) {
      const plans = draft.model.plans;
      if (!plans || plans.length <= 1) return;
      const [movedItem] = plans.splice(indexToChange, 1);
      const newTarget = indexTarget > indexToChange ? indexTarget - 1 : indexTarget;
      plans.splice(newTarget, 0, movedItem);
      resetIndexes(plans);
    });
  },
  removePrice(index) {
    set(function removePrice(draft) {
      draft.model.plans.splice(index, 1);
      resetIndexes(draft.model.plans);
    });
  },
  addPrice() {
    set(function addPrice(draft) {
      const plan = createNewDefaultPlan(draft.model.plans.length);
      if (draft.priceModelType === 'paid' && draft.model.payment_type === 'salesforce_provided') {
        plan.active = false;
      }
      draft.model.plans.push(plan);
    });
  },
  updateShowInputErrors() {
    set(function updateShowInputErrors(draft) {
      if (draft.model && Array.isArray(draft.model.plans)) {
        draft.model.plans = draft.model.plans.map(plan => ({
          ...plan,
          __UI_STATE__: createUIStateForPlan(plan)
        }));
      }
    });
  },
  /* istanbul ignore next */
  setActive(index, value) {
    set(function setActive(draft) {
      if (draft.model.plans && draft.model.plans.length > 0) {
        draft.model.plans[index].active = value;
      }
    });
  },
  /* istanbul ignore next */
  setDisplayPlanNames(value) {
    set(function setDisplayPlanNames(draft) {
      draft.model.display_plan_names = value;
    });
  },
  validate(listingId, listingExternalId, org62AccountId) {
    const {
      model,
      priceModelType,
      commerceProductId
    } = get();
    if (!model || !priceModelType) {
      return Promise.resolve({
        isValid: false,
        errors: []
      });
    }
    const record = pricingModelToRecord(model, priceModelType, listingId, listingExternalId, org62AccountId, commerceProductId);
    return validatePricing(record);
  },
  async setStripeIds(listingId) {
    set(function disableCheckShouldChange(draft) {
      draft.cachedHasChanged = get().hasChanged;
    });
    const pricingData = await retrievePricing(listingId);
    if (isPricingProjectionValid(pricingData)) {
      const model = pricingData.model;
      // We store UI STATE on the plans array to make it easier to handle re-ordering and displaying input errors, this field gets removed in pricingModelToRecord
      if (model && Array.isArray(model.plans)) {
        model.plans = model.plans.map(plan => ({
          ...plan,
          __UI_STATE__: createUIStateForPlan(plan)
        }));
      }
      set(function setStripeIds(draft) {
        var _pricingData$commerce2;
        draft.model = model;
        draft.commerceProductId = (_pricingData$commerce2 = pricingData.commerce_external_id) !== null && _pricingData$commerce2 !== void 0 ? _pricingData$commerce2 : '';
      });
      set(function disableCheckShouldChange(draft) {
        draft.hasChanged = get().cachedHasChanged;
      });
    }
  },
  async syncPrices(listingId, listingExternalId, org62AccountId, isDraft) {
    const {
      model,
      priceModelType,
      commerceProductId
    } = get();
    const record = pricingModelToRecord(model, priceModelType, listingId, listingExternalId, org62AccountId, commerceProductId);
    this.setIsSaving(true);
    try {
      await savePricingService(record, isDraft, true);
      this.resetHasChanged();
      this.setIsSaving(false);
    } catch (ex) {
      console.error(`savePricing FAILED: ${JSON.stringify(ex)}`);
      this.setIsSaving(false);
      notifyFailure(i18nStore.getState().i18nStaticCache.notifications.syncPlansFailed);
    }
  },
  async save(listingId, listingExternalId, org62AccountId, isDraft = true, syncPrices = false, isSubmitForApproval = false) {
    const {
      model,
      priceModelType,
      hasChanged,
      commerceProductId
    } = get();
    const record = pricingModelToRecord(model, priceModelType, listingId, listingExternalId, org62AccountId, commerceProductId);
    if (priceModelType && (hasChanged || isSubmitForApproval)) {
      this.setIsSaving(true);
      try {
        const result = await savePricingService(record, isDraft, syncPrices, isSubmitForApproval);
        if (isSubmitForApproval) {
          retrievePricing(listingId).then(data => {
            if (data.approval) {
              approvalsStore.setApprovalStatus(data.approval.status);
            }
          });
          approvalsStore.setApprovalStatus(BUSINESS_APPROVAL_STATUSES.Submitted);
        }
        this.resetHasChanged();
        this.setIsSaving(false);
        return result;
      } catch (ex) {
        console.error(`savePricing FAILED: ${JSON.stringify(ex)}`);
        notifyFailure(i18nStore.getState().i18nStaticCache.notifications.syncPlansFailed);
        this.setIsSaving(false);
      }
    }
    this.resetHasChanged();
    return record;
  },
  /* istanbul ignore next */
  setRequireResubmission() {
    set(function setRequireResubmission(draft) {
      draft.isResubmissionRequired = true;
    });
  },
  resetRequireResubmission() {
    set(function resetRequireResubmission(draft) {
      draft.isResubmissionRequired = false;
    });
  }
}), 'PRICING', typeof process !== 'undefined' && ((_process$env = process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'development');
export const WirePricingStore = createWireStore(pricingStore);
routerStore.subscribe(pricingStore.updateShowInputErrors, selectShowInputErrors);
export function selectModel(state) {
  return state.model;
}
function checkShouldChange() {
  if (!pricingStore.getState().hasChanged && !pricingStore.getState().isLoading) {
    pricingStore.change();
  }
}
pricingStore.subscribe(checkShouldChange, ({
  priceModelType,
  model,
  paymentType
}) => ({
  priceModelType,
  model,
  paymentType
}), equalityFn);
export function resetIndexes(plans) {
  for (let i = 0; i < plans.length; i++) {
    plans[i].index = i;
  }
}
export function reOrderByIndex(plans) {
  plans.sort((a, b) => {
    // In case one of the elements don't have index property I will leave to the end of the array
    if (a.index == null || b.index == null) {
      return 1;
    }
    return a.index - b.index;
  });
}
export function shouldTriggerChangeCaseAction() {
  return isApprovalStatusForResubmission() && (isPriceModelChangingFromFree() || isPriceModelChangingToFree() || isPaymentTypeChanged() || pricingStore.getState().isResubmissionRequired);
}
function isPriceModelChangingFromFree() {
  return pricingStore.getState().initialPriceModelType !== undefined && pricingStore.getState().initialPriceModelType !== pricingStore.getState().priceModelType && pricingStore.getState().initialPriceModelType === ModelTypes.Free;
}
function isPriceModelChangingToFree() {
  return pricingStore.getState().initialPriceModelType !== undefined && pricingStore.getState().initialPriceModelType !== pricingStore.getState().priceModelType && pricingStore.getState().priceModelType === ModelTypes.Free;
}
function isPaymentTypeChanged() {
  return pricingStore.getState().initialPaymentType !== '' && pricingStore.getState().paymentType !== '' && pricingStore.getState().initialPaymentType !== pricingStore.getState().paymentType;
}
function isApprovalStatusForResubmission() {
  const approvalStatus = selectApprovalStatus(approvalsStore.getState());
  return approvalStatus === BUSINESS_APPROVAL_STATUSES.Approved || approvalStatus === BUSINESS_APPROVAL_STATUSES['In Review'] || approvalStatus === BUSINESS_APPROVAL_STATUSES.Submitted;
}
function doesPlansHaveOneTime(plans) {
  // Iterates through plans looking for any instance where an item has one-time frequency
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
  return plans.some(plan => plan.frequency === 'one-time');
}
approvalsStore.subscribe(isApprovalStatusForResubmission, selectApprovalStatus);